// SelectorsEditor.tsx

import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  FormGroup,
  Badge,
  Tooltip,
  Row,
  Col,
  Button,
  Alert,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  Selector,
  Selectors,
  LodashFunction,
  carDetailsKeys,
  CarDetails,
  contextTranslations,
  fieldTranslations,
  carDetailsFields as carDetailsFieldsInitial,
} from "./types";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { lodashFunctionsList } from "./lodashFunctions";
import "./SelectorsEditor.css";
import { v4 as uuid } from "uuid";
import LodashFunctionSelector from "./LodashFnSelector";
import ValueSelector from "./ValueSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faTimes,
  faPlus,
  faCode,
  faChevronDown,
  faChevronRight,
  faFilter,
  faSearch,
  faArrowRight,
  faArrowLeft,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  faBrain,
  faLayerGroup,
  faGripVertical, // Ajout de l'icône pour le drag and drop
} from "@fortawesome/free-solid-svg-icons";
import Fuse from "fuse.js";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"; // Ajout de l'import pour le drag and drop

// Importer les bibliothèques de traitement de texte
import * as stringSimilarity from "string-similarity";
import lunr from "lunr";

interface SelectorsEditorProps {
  selectors: Selectors;
  setSelectors: (selectors: Selectors) => void;
  addMainSelector: ({ name }: { name: string }) => void;
  removeMainSelector: (name: string) => void;
  carDetailsFields: typeof carDetailsFieldsInitial;
  parsedData: any;
  type: "JSON" | "HTML";
  errors?: any;
  rawData?: any;
}

// Composant personnalisé pour afficher les options avec tooltip
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OptionWithTooltip: React.FC<any> = (props) => {
  const { data, innerRef, innerProps } = props;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  return (
    <div ref={innerRef} {...innerProps} style={{ position: "relative" }}>
      <components.Option {...props}>
        <span>{data.label}</span>
        <i
          className="fas fa-info-circle ml-2"
          id={`tooltip-${data.value}`}
          style={{ cursor: "pointer" }}
          onMouseEnter={toggleTooltip}
          onMouseLeave={toggleTooltip}
        ></i>
        <Tooltip
          placement="right"
          isOpen={tooltipOpen}
          target={`tooltip-${data.value}`}
          toggle={toggleTooltip}
        >
          <pre style={{ whiteSpace: "pre-wrap" }}>{data.description}</pre>
        </Tooltip>
      </components.Option>
    </div>
  );
};

// Interface pour les résultats de recherche NLP
interface NlpSearchResult {
  path: string;
  value: any;
  similarity: number;
}

// Interface pour les éléments à indexer par Fuse.js
interface FuseItem {
  path: string;
  key?: string;
  value: any;
  text: string;
  keywords?: string[]; // Mots-clés supplémentaires pour la recherche
  context?: string; // Contexte du champ
  comment?: string; // Commentaire descriptif du champ
  tokens?: string[]; // Ajout des tokens pour l'analyse NLP
}

// Interface pour les résultats multi-moteurs
interface MultiEngineSearchResult extends NlpSearchResult {
  engines: {
    fuse?: number;
    lunr?: number;
    similarity?: number;
  };
  combinedScore: number;
}

const SelectorsEditor: React.FC<SelectorsEditorProps> = ({
  selectors,
  setSelectors,
  addMainSelector,
  removeMainSelector,
  carDetailsFields,
  parsedData = {},
  type,
  errors = {},
  rawData = {},
}) => {
  const handleSelectorChange = (
    fieldName: keyof Selectors,
    updatedSelector: Partial<Selector>,
  ) => {
    setSelectors({
      ...selectors,
      [fieldName]: {
        ...selectors[fieldName],
        type: selectors[fieldName]?.type || "Path",
        ...updatedSelector,
      },
    });
  };

  const [inputValue, setInputValue] = useState("");

  const handleLodashAppendFn = (
    fieldName: keyof Selectors,
    lodashFn: LodashFunction,
  ) => {
    setSelectors({
      ...selectors,
      [fieldName]: {
        ...selectors[fieldName],
        lodashFns: [...(selectors[fieldName]?.lodashFns || []), lodashFn],
      },
    });
  };

  const handleLodashRemoveFn = (fieldName: keyof Selectors, id: string) => {
    setSelectors({
      ...selectors,
      [fieldName]: {
        ...selectors[fieldName],
        lodashFns: selectors[fieldName]?.lodashFns?.filter(
          (fn) => fn.id !== id,
        ),
      },
    });
  };

  const handleLodashFnChange = (
    fieldName: keyof Selectors,
    lodashFns: LodashFunction[],
  ) => {
    setSelectors({
      ...selectors,
      [fieldName]: {
        ...selectors[fieldName],
        lodashFns,
      },
    });
  };

  const handleAddMainSelector = () => {
    if (inputValue.trim() === "") {
      return;
    }
    addMainSelector({ name: inputValue });
    setInputValue("");
  };

  const getStatus = (fieldName: keyof Selectors) => {
    const selectorValue = selectors[fieldName]?.value?.trim();
    const parsedValue = parsedData[fieldName]?.toString().trim();

    if (!selectorValue || !parsedValue) {
      return "warning";
    }
    return "success";
  };

  const getStatusText = (fieldName: keyof Selectors) => {
    const selectorValue = selectors[fieldName]?.value?.trim();
    const parsedValue = parsedData[fieldName]?.toString().trim();

    if (!selectorValue && !parsedValue) {
      return "Sélecteur et Résultat manquants";
    }
    if (!selectorValue) {
      return "Sélecteur manquant";
    }
    if (!parsedValue) {
      return "Résultat du parsing manquant";
    }
    return "Complet";
  };

  // Gestion des Tooltips pour les champs principaux
  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>(
    {},
  );

  const toggleTooltipState = (fieldName: string) => {
    setTooltipOpen({
      ...tooltipOpen,
      [fieldName]: !tooltipOpen[fieldName],
    });
  };



  // Nouveaux états pour l'éditeur JSON
  const [isJsonEditor, setIsJsonEditor] = useState(false);
  const [jsonInput, setJsonInput] = useState(
    JSON.stringify(selectors, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  // État pour gérer les contextes dépliés
  const [expandedContexts, setExpandedContexts] = useState<{
    [key: string]: boolean;
  }>(() => {
    // Par défaut, tous les contextes sont dépliés
    // Extraire les contextes uniques de carDetailsFields
    const uniqueContexts = Array.from(
      new Set(carDetailsFields.map((field) => field.context)),
    );

    return uniqueContexts.reduce(
      (acc, context) => {
        acc[context] = true;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
  });

  // Ajout du filtre pour les champs
  const [fieldFilter, setFieldFilter] = useState<string>("all");

  // Fonction pour filtrer les champs selon le critère sélectionné
  const filterFields = (fields: typeof carDetailsFields) => {
    return fields.filter((field) => {
      const fieldName = field.name as keyof Selectors;
      const fieldSelector = selectors[fieldName];
      const hasParsedResult = !!parsedData[fieldName];

      switch (fieldFilter) {
        case "configured":
          return (
            fieldSelector &&
            fieldSelector.value &&
            fieldSelector.value.trim() !== ""
          );
        case "unconfigured":
          return (
            !fieldSelector ||
            !fieldSelector.value ||
            fieldSelector.value.trim() === ""
          );
        case "withResult":
          return hasParsedResult;
        case "withoutResult":
          return !hasParsedResult;
        default:
          return true; // "all"
      }
    });
  };

  // Fonction pour basculer l'état d'un contexte
  const toggleContext = (context: string) => {
    setExpandedContexts((prev) => ({
      ...prev,
      [context]: !prev[context],
    }));
  };

  // Synchroniser jsonInput avec les sélecteurs si l'éditeur JSON est ouvert
  useEffect(() => {
    if (isJsonEditor) {
      setJsonInput(JSON.stringify(selectors, null, 2));
    }
  }, [selectors, isJsonEditor]);

  // Regrouper les champs par contexte
  const fieldsByContext = carDetailsFields.reduce<{
    [context: string]: typeof carDetailsFields;
  }>((acc, field) => {
    if (!acc[field.context]) {
      acc[field.context] = [];
    }
    acc[field.context].push(field);
    return acc;
  }, {});

  // Nouveaux états pour la recherche NLP
  const [nlpModalOpen, setNlpModalOpen] = useState(false);
  const [currentSearchField, setCurrentSearchField] = useState<string>("");
  const [nlpSearchResults, setNlpSearchResults] = useState<NlpSearchResult[]>(
    [],
  );
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0);
  const resultListRef = useRef<HTMLDivElement>(null);
  const [searchInput, setSearchInput] = useState<string>("");

  // Nouveaux états pour la boucle de parcours des champs
  const [isLooping, setIsLooping] = useState(false);
  const [currentLoopField, setCurrentLoopField] = useState<string | null>(null);
  const [loopInterval, setLoopInterval] = useState<NodeJS.Timeout | null>(null);
  const [, setLoopCounter] = useState<number>(0);
  const [fieldScores, setFieldScores] = useState<Record<string, number>>({});

  // Fonctions utilitaires pour le traitement du texte
  const tokenize = (text: string): string[] => {
    return text
      .toLowerCase()
      .split(/\W+/)
      .filter((token) => token.length > 1);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const stem = (word: string): string => {
    // Version simplifiée du stemming - supprime juste les suffixes communs en français
    const suffixes = [
      "ement",
      "ment",
      "tion",
      "sion",
      "age",
      "eur",
      "euse",
      "eux",
      "iste",
      "isme",
      "er",
      "ir",
      "re",
      "ant",
      "é",
      "s",
    ];
    let stemmed = word.toLowerCase();

    for (const suffix of suffixes) {
      if (stemmed.length > suffix.length + 3 && stemmed.endsWith(suffix)) {
        return stemmed.slice(0, stemmed.length - suffix.length);
      }
    }

    return stemmed;
  };

  // Fonction pour calculer la similarité sémantique entre deux textes
  const calculateTextSimilarity = (text1: string, text2: string): number => {
    // Utiliser string-similarity pour la similarité de base
    const baseSimilarity = stringSimilarity.compareTwoStrings(
      text1.toLowerCase(),
      text2.toLowerCase(),
    );

    // Calculer la similarité basée sur les tokens communs
    const tokens1 = tokenize(text1);
    const tokens2 = tokenize(text2);

    if (tokens1.length === 0 || tokens2.length === 0) {
      return baseSimilarity;
    }

    // Calculer la similarité de Jaccard entre les tokens
    const set1 = new Set(tokens1);
    const set2 = new Set(tokens2);

    // Convertir en arrays pour manipulation
    const array1 = Array.from(set1);
    const array2 = Array.from(set2);

    // Calculer l'intersection
    let intersectionCount = 0;
    for (const token of array1) {
      if (set2.has(token)) {
        intersectionCount++;
      }
    }

    // Calculer l'union
    const unionCount = array1.length + array2.length - intersectionCount;

    // Similarité de Jaccard
    const jaccardSimilarity =
      unionCount === 0 ? 0 : intersectionCount / unionCount;

    // Combiner les deux méthodes de similarité
    return baseSimilarity * 0.6 + jaccardSimilarity * 0.4;
  };

  // Nouvelle fonction pour détecter les abréviations et les combinaisons de mots
  const detectAbbreviationsAndCombinations = (
    searchTerm: string,
    fieldNames: string[],
  ): string[] => {
    const results: string[] = [];
    const searchTermLower = searchTerm.toLowerCase();

    // 1. Vérifier si le terme de recherche est une abréviation d'un champ
    for (const fieldName of fieldNames) {
      const fieldNameLower = fieldName.toLowerCase();

      // Cas 1: Le terme de recherche est un préfixe du nom du champ
      if (
        fieldNameLower.startsWith(searchTermLower) &&
        fieldNameLower !== searchTermLower
      ) {
        results.push(fieldName);
        continue;
      }

      // Cas 2: Le terme de recherche est une abréviation (contient les premières lettres des mots composés)
      // Par exemple, "doorC" pourrait correspondre à "doorCount"
      if (
        searchTermLower.length >= 3 &&
        fieldNameLower.includes(searchTermLower)
      ) {
        // Vérifier si le terme de recherche est une sous-chaîne continue du nom du champ
        if (fieldNameLower.includes(searchTermLower)) {
          results.push(fieldName);
          continue;
        }
      }

      // Cas 3: Décomposer les noms de champs en camelCase ou PascalCase
      // Par exemple, "doorCount" devient ["door", "count"]
      const fieldWords = fieldNameLower
        .split(/(?=[A-Z])/)
        .map((w) => w.toLowerCase());

      // Vérifier si le terme de recherche correspond à l'un des mots décomposés
      if (fieldWords.some((word) => word === searchTermLower)) {
        results.push(fieldName);
        continue;
      }

      // Cas 4: Vérifier si le terme de recherche est une combinaison partielle des mots du champ
      // Par exemple, "door" pourrait correspondre à "doorCount" car "door" est un préfixe
      if (
        fieldWords.some(
          (word) =>
            word.startsWith(searchTermLower) && word !== searchTermLower,
        )
      ) {
        results.push(fieldName);
        continue;
      }
    }

    return results;
  };

  // Fonction améliorée pour rechercher des termes similaires dans le JSON
  const findSimilarTerms = (searchTerm: string, jsonData: any) => {
    if (!searchTerm || !jsonData) {
      console.log(
        "Recherche impossible: terme vide ou données JSON manquantes",
      );
      return [];
    }

    // Essayer d'utiliser les données affichées dans l'interface si disponibles
    let dataToSearch = jsonData;

    // Si les données sont dans une structure spécifique, essayons de les extraire
    if (jsonData.data && typeof jsonData.data === "object") {
      dataToSearch = jsonData.data;
    } else if (jsonData.content && typeof jsonData.content === "object") {
      dataToSearch = jsonData.content;
    } else if (jsonData.result && typeof jsonData.result === "object") {
      dataToSearch = jsonData.result;
    }

    // Rechercher également dans les propriétés imbriquées potentielles
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const potentialNestedProps = [
      "items",
      "elements",
      "results",
      "values",
      "children",
      "list",
      "records",
      "props",
      "pageProps",
      "listingDetails",
      "seller",
      "details",
      "info",
      "data",
      "metadata",
      "attributes",
      "properties",
      "features",
      "specs",
      "specifications",
    ];

    // Fonction récursive pour trouver des propriétés imbriquées intéressantes
    const findNestedProps = (
      obj: any,
      currentPath: string = "",
      depth: number = 0,
    ) => {
      if (!obj || typeof obj !== "object" || depth > 5) return;

      Object.entries(obj).forEach(([key, value]) => {
        const newPath = currentPath ? `${currentPath}.${key}` : key;

        // Explorer récursivement
        if (typeof value === "object" && value !== null) {
          findNestedProps(value, newPath, depth + 1);
        }
      });
    };

    // Explorer les propriétés imbriquées intéressantes
    findNestedProps(dataToSearch);

    // Extraire toutes les clés et valeurs du JSON pour l'indexation
    const items: FuseItem[] = [];
    const visitedPaths = new Set<string>();

    // Préparation des données NLP
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const searchTermTokens = tokenize(searchTerm);

    // Fonction récursive pour parcourir l'objet JSON et extraire les éléments
    const extractItems = (obj: any, path: string = "", depth: number = 0) => {
      if (!obj || typeof obj !== "object") return;

      // Limiter la profondeur de récursion pour éviter les boucles infinies
      if (depth > 20) {
        return;
      }

      // Éviter les boucles infinies avec des objets circulaires
      if (visitedPaths.has(path)) return;
      if (path) visitedPaths.add(path);

      // Extraire les segments du chemin pour les utiliser comme mots-clés supplémentaires
      const pathSegments = path.split(/[.[\]]+/).filter(Boolean);
      const pathKeywords: string[] = [];

      // Pour chaque segment du chemin, chercher des traductions possibles
      pathSegments.forEach((segment) => {
        // Vérifier si le segment est un nom de champ connu
        if (fieldTranslations[segment]) {
          pathKeywords.push(...fieldTranslations[segment]);
        }

        // Ajouter le segment lui-même comme mot-clé
        if (segment.length > 2) {
          // Ignorer les segments trop courts
          pathKeywords.push(segment);
        }
      });

      if (Array.isArray(obj)) {
        // Traiter chaque élément du tableau
        obj.forEach((item, idx) => {
          const newPath = path ? `${path}[${idx}]` : `[${idx}]`;

          // Ajouter les valeurs primitives à l'index
          if (typeof item !== "object" && item !== null) {
            const itemText = String(item);
            // Générer les tokens pour l'analyse
            const tokens = tokenize(itemText);

            // Ajouter les tokens du chemin
            const pathTokens = tokenize(pathKeywords.join(" "));
            const combinedTokens = [...tokens, ...pathTokens];

            items.push({
              path: newPath,
              value: item,
              text: itemText,
              keywords: pathKeywords, // Ajouter les mots-clés du chemin
              tokens: combinedTokens,
            });
          }

          // Parcourir récursivement les objets/tableaux
          if (item && typeof item === "object") {
            extractItems(item, newPath, depth + 1);
          }
        });

        // Ajouter également le tableau lui-même comme élément
        if (path) {
          items.push({
            path: path,
            value: obj,
            text: `Tableau de ${obj.length} éléments`,
            keywords: pathKeywords,
            tokens: tokenize(pathKeywords.join(" ")),
          });
        }
      } else {
        // Traiter chaque propriété de l'objet
        Object.entries(obj).forEach(([key, value]) => {
          const newPath = path ? `${path}.${key}` : key;

          // Trouver les informations de contexte et de commentaire pour cette clé
          const fieldInfo = carDetailsFields.find(
            (field) => field.name === key,
          );
          const context = fieldInfo?.context || "";
          const comment = fieldInfo?.comment || "";

          // Générer des mots-clés supplémentaires basés sur le contexte et les traductions
          let keywords: string[] = [...pathKeywords]; // Inclure les mots-clés du chemin

          // Ajouter les traductions de contexte depuis le dictionnaire complet
          if (context && contextTranslations[context]) {
            keywords = [...keywords, ...contextTranslations[context]];
          }

          // Ajouter les traductions spécifiques au champ depuis le dictionnaire complet
          if (fieldTranslations[key]) {
            keywords = [...keywords, ...fieldTranslations[key]];
          }

          // Extraire des mots-clés du commentaire
          if (comment) {
            // Extraire les mots significatifs du commentaire (plus de 3 lettres)
            const commentWords = comment
              .split(/\s+/)
              .filter((word) => word.length > 3)
              .map((word) => word.toLowerCase());

            keywords = [...keywords, ...commentWords];
          }

          // Éliminer les doublons
          keywords = Array.from(new Set(keywords));

          // Générer les tokens pour la clé
          const keyTokens = tokenize(key);

          // Générer les tokens pour les mots-clés
          const allKeywordsText = keywords.join(" ").toLowerCase();
          const keywordTokens = tokenize(allKeywordsText);

          // Combiner tous les tokens
          const allTokens = [...keyTokens, ...keywordTokens];

          // Ajouter la clé à l'index avec les mots-clés enrichis
          items.push({
            path: newPath,
            key,
            value,
            text: key,
            keywords,
            context,
            comment,
            tokens: allTokens,
          });

          // Ajouter les valeurs primitives à l'index
          if (typeof value !== "object" && value !== null) {
            const valueText = String(value);
            // Générer les tokens pour la valeur
            const valueTokens = tokenize(valueText);

            // Combiner avec les tokens de la clé
            const combinedTokens = [...allTokens, ...valueTokens];

            items.push({
              path: newPath,
              key,
              value,
              text: valueText,
              keywords,
              context,
              comment,
              tokens: combinedTokens,
            });
          } else if (value !== null) {
            // Pour les objets et tableaux, ajouter également une entrée avec leur représentation JSON
            try {
              // Limiter la taille de la représentation JSON pour éviter des tokens trop longs
              const jsonText = JSON.stringify(value).substring(0, 500);
              const valueTokens = tokenize(jsonText);

              // Combiner avec les tokens de la clé
              const combinedTokens = [...allTokens, ...valueTokens];

              // Ajouter une entrée pour la représentation JSON de l'objet/tableau
              items.push({
                path: newPath,
                key,
                value,
                text: jsonText,
                keywords,
                context,
                comment,
                tokens: combinedTokens,
              });
            } catch (e) {
              console.log(`Impossible de convertir en JSON pour ${newPath}`);
            }
          }

          // Parcourir récursivement les objets/tableaux
          if (value && typeof value === "object") {
            extractItems(value, newPath, depth + 1);

            // Si c'est un objet, explorer également ses propriétés individuellement
            if (!Array.isArray(value)) {
              // Extraire les propriétés de premier niveau de l'objet pour une meilleure indexation
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (typeof subValue !== "object" && subValue !== null) {
                  const subPath = `${newPath}.${subKey}`;
                  const subText = String(subValue);

                  // Générer des tokens pour cette sous-propriété
                  const subTokens = tokenize(subText);
                  const subKeyTokens = tokenize(subKey);

                  // Combiner tous les tokens
                  const allSubTokens = [
                    ...allTokens,
                    ...subKeyTokens,
                    ...subTokens,
                  ];

                  // Ajouter une entrée pour cette sous-propriété
                  items.push({
                    path: subPath,
                    key: subKey,
                    value: subValue,
                    text: subText,
                    keywords: [...keywords, subKey], // Ajouter le nom de la sous-propriété aux mots-clés
                    context,
                    comment,
                    tokens: allSubTokens,
                  });
                }
              });
            }
          }
        });

        // Ajouter également l'objet lui-même comme élément si ce n'est pas l'objet racine
        if (path) {
          // Ajouter une représentation JSON complète de l'objet pour la recherche
          try {
            const jsonText = JSON.stringify(obj).substring(0, 1000); // Limiter à 1000 caractères
            const jsonTokens = tokenize(jsonText);

            items.push({
              path: path,
              value: obj,
              text: `Objet avec ${Object.keys(obj).length} propriétés: ${jsonText}`,
              keywords: pathKeywords,
              tokens: [...tokenize(pathKeywords.join(" ")), ...jsonTokens],
            });
          } catch (e) {
            // En cas d'erreur, ajouter une version simplifiée
            items.push({
              path: path,
              value: obj,
              text: `Objet avec ${Object.keys(obj).length} propriétés`,
              keywords: pathKeywords,
              tokens: tokenize(pathKeywords.join(" ")),
            });
          }
        }
      }
    };

    // Appeler la fonction extractItems avec les données à rechercher
    extractItems(dataToSearch);

    // Afficher quelques exemples d'éléments indexés pour le débogage
    if (!(items.length > 0)) {
      console.log("ATTENTION: Aucun élément n'a été indexé!");

      // Tentative de recherche directe dans les données brutes
      console.log("Tentative de recherche directe dans les données brutes");
      const jsonString = JSON.stringify(dataToSearch).toLowerCase();
      if (jsonString.includes(searchTerm.toLowerCase())) {
        console.log(`Le terme "${searchTerm}" existe dans les données brutes!`);

        // Créer un résultat artificiel pour montrer que le terme existe
        return [
          {
            path: "Terme trouvé dans les données brutes",
            value: searchTerm,
            similarity: 1.0,
            engines: { fuse: 1.0, lunr: 1.0, similarity: 1.0 },
            combinedScore: 1.0,
          } as MultiEngineSearchResult,
        ];
      } else {
        console.log(
          `Le terme "${searchTerm}" n'existe pas dans les données brutes.`,
        );
      }

      return [];
    }

    // Recherche intelligente améliorée: vérifier si le terme de recherche est similaire à un champ connu
    // et ajouter ses synonymes à la recherche
    let enhancedSearchTerms = [searchTerm.toLowerCase()];
    let foundExactMatch = false;

    // Récupérer tous les termes de traduction pour la détection d'abréviations
    const allTranslationTerms: string[] = [];

    // Extraire tous les termes de traduction des fieldTranslations
    Object.values(fieldTranslations).forEach((translations) => {
      translations.forEach((term) => {
        if (!allTranslationTerms.includes(term)) {
          allTranslationTerms.push(term);
        }
      });
    });

    // Détecter les abréviations et les combinaisons de mots dans les termes de traduction
    const abbreviationMatches = detectAbbreviationsAndCombinations(
      searchTerm,
      allTranslationTerms,
    );

    if (abbreviationMatches.length > 0) {
      // Ajouter les termes correspondants à la recherche
      enhancedSearchTerms = [...enhancedSearchTerms, ...abbreviationMatches];

      // Trouver les champs associés à ces termes de traduction
      for (const [field, translations] of Object.entries(fieldTranslations)) {
        if (abbreviationMatches.some((match) => translations.includes(match))) {
          enhancedSearchTerms.push(field);
          enhancedSearchTerms = [...enhancedSearchTerms, ...translations];
        }
      }
    }

    // Vérifier d'abord les correspondances exactes
    for (const [field, translations] of Object.entries(fieldTranslations)) {
      // Vérifier si le terme correspond exactement à un nom de champ ou une de ses traductions
      if (
        field.toLowerCase() === searchTerm.toLowerCase() ||
        translations.some(
          (term) => term.toLowerCase() === searchTerm.toLowerCase(),
        )
      ) {
        // Ajouter tous les synonymes de ce champ à la recherche
        enhancedSearchTerms = [...enhancedSearchTerms, ...translations];
        // Ajouter aussi le nom du champ lui-même
        enhancedSearchTerms.push(field);
        foundExactMatch = true;
        break;
      }
    }

    // Si aucune correspondance exacte n'est trouvée, chercher des correspondances partielles
    if (!foundExactMatch) {
      // Chercher des correspondances partielles dans les noms de champs et leurs traductions
      for (const [field, translations] of Object.entries(fieldTranslations)) {
        // Vérifier si le terme est contenu dans un nom de champ
        if (field.toLowerCase().includes(searchTerm.toLowerCase())) {
          enhancedSearchTerms.push(field);
          enhancedSearchTerms = [...enhancedSearchTerms, ...translations];
          continue;
        }

        // Vérifier si le terme est contenu dans une des traductions
        for (const translation of translations) {
          if (translation.toLowerCase().includes(searchTerm.toLowerCase())) {
            enhancedSearchTerms.push(field);
            enhancedSearchTerms = [...enhancedSearchTerms, ...translations];
            break;
          }
        }
      }
    }

    // Vérifier également les contextes
    for (const [ctx, translations] of Object.entries(contextTranslations)) {
      if (
        ctx.toLowerCase() === searchTerm.toLowerCase() ||
        translations.some(
          (term) => term.toLowerCase() === searchTerm.toLowerCase(),
        )
      ) {
        // Ajouter tous les synonymes de ce contexte à la recherche
        enhancedSearchTerms = [...enhancedSearchTerms, ...translations];
        // Ajouter aussi le nom du contexte lui-même
        enhancedSearchTerms.push(ctx);
      }
    }

    // Ajouter des termes de recherche supplémentaires pour les champs courants
    // Cela permet de trouver des correspondances même si le terme n'est pas dans les traductions
    const commonFields = [
      "id",
      "name",
      "title",
      "description",
      "price",
      "value",
      "amount",
      "count",
      "date",
      "time",
      "address",
      "location",
      "city",
      "country",
      "state",
      "zip",
      "postal",
      "code",
      "phone",
      "email",
      "url",
      "link",
      "website",
      "image",
      "photo",
      "picture",
      "file",
      "document",
      "type",
      "category",
      "tag",
      "label",
      "status",
      "condition",
      "brand",
      "model",
      "year",
      "color",
      "size",
      "weight",
      "height",
      "width",
      "length",
      "depth",
      "volume",
      "area",
      "quantity",
      "unit",
      "seller",
      "buyer",
      "owner",
      "user",
      "customer",
      "client",
      "contact",
      "person",
      "company",
      "business",
      "organization",
      "department",
      "service",
      "product",
      "item",
      "article",
      "good",
      "merchandise",
      "stock",
      "inventory",
      "supply",
      "detail",
      "specification",
      "feature",
      "characteristic",
      "attribute",
      "property",
      "parameter",
      "setting",
      "configuration",
      "option",
      "choice",
      "selection",
      "alternative",
      "variant",
      "version",
      "edition",
      "release",
      "update",
      "revision",
      "modification",
      "change",
      "alteration",
      "adjustment",
      "correction",
      "fix",
      "repair",
      "maintenance",
      "service",
      "support",
      "assistance",
      "help",
      "aid",
      "guidance",
      "instruction",
      "direction",
      "indication",
      "suggestion",
      "recommendation",
      "advice",
      "counsel",
      "tip",
      "hint",
      "clue",
      "lead",
      "trace",
      "track",
      "trail",
      "path",
      "route",
      "way",
      "course",
      "direction",
      "bearing",
      "heading",
      "orientation",
      "position",
      "location",
      "place",
      "spot",
      "site",
      "venue",
      "area",
      "region",
      "zone",
      "sector",
      "district",
      "quarter",
      "neighborhood",
      "vicinity",
      "surroundings",
      "environment",
      "context",
      "setting",
      "background",
      "framework",
      "structure",
      "system",
      "network",
      "grid",
      "matrix",
      "array",
      "collection",
      "set",
      "group",
      "cluster",
      "bunch",
      "batch",
      "lot",
      "series",
      "sequence",
      "succession",
      "progression",
      "chain",
      "string",
      "line",
      "row",
      "column",
      "file",
      "rank",
      "tier",
      "level",
      "layer",
      "stratum",
      "floor",
      "story",
      "deck",
      "platform",
      "stage",
      "phase",
      "period",
      "era",
      "epoch",
      "age",
      "time",
      "date",
      "moment",
      "instant",
      "second",
      "minute",
      "hour",
      "day",
      "week",
      "month",
      "year",
      "decade",
      "century",
      "millennium",
    ];

    // Ajouter les champs communs qui contiennent le terme de recherche
    const matchingCommonFields = commonFields.filter(
      (field) =>
        field.toLowerCase().includes(searchTerm.toLowerCase()) ||
        searchTerm.toLowerCase().includes(field.toLowerCase()),
    );

    if (matchingCommonFields.length > 0) {
      enhancedSearchTerms = [...enhancedSearchTerms, ...matchingCommonFields];
    }

    // Éliminer les doublons dans les termes de recherche
    enhancedSearchTerms = Array.from(new Set(enhancedSearchTerms));

    // 1. RECHERCHE AVEC FUSE.JS
    const fuseOptions = {
      includeScore: true,
      keys: [
        "text",
        "keywords", // Rechercher aussi dans les mots-clés générés
        "context", // Rechercher dans le contexte
        "comment", // Rechercher dans le commentaire
      ],
      threshold: 0.6, // Augmenté pour être plus permissif (0-1, plus élevé = plus de résultats)
      distance: 200, // Augmenté pour permettre plus de différences
      minMatchCharLength: 1, // Réduit pour permettre des correspondances plus courtes
      shouldSort: true,
      findAllMatches: true,
      ignoreLocation: true,
      useExtendedSearch: true, // Activer la recherche étendue
    };

    const fuse = new Fuse(items, fuseOptions);

    // Rechercher avec tous les termes enrichis et fusionner les résultats
    let fuseSearchResults: Array<{
      item: FuseItem;
      refIndex: number;
      score?: number;
    }> = [];

    for (const term of enhancedSearchTerms) {
      const termResults = fuse.search(term);
      fuseSearchResults = [...fuseSearchResults, ...termResults];
    }

    // Éliminer les doublons dans les résultats (même item)
    const uniqueFuseResults = fuseSearchResults.filter(
      (result, index, self) =>
        index === self.findIndex((r) => r.item.path === result.item.path),
    );

    // 2. RECHERCHE AVEC LUNR.JS
    // Créer un index Lunr pour la recherche full-text
    const lunrIndex = lunr(function (this: lunr.Builder) {
      // Configurer l'index
      this.ref("path");
      this.field("text");
      this.field("keywords");
      this.field("context");
      this.field("comment");

      // Ajouter les documents à l'index
      items.forEach((item) => {
        // Préparer un document pour l'indexation
        const doc = {
          path: item.path,
          text: item.text || "",
          keywords: (item.keywords || []).join(" "),
          context: item.context || "",
          comment: item.comment || "",
        };

        this.add(doc);
      });
    });

    // Effectuer la recherche avec Lunr
    let lunrResults: Array<{ ref: string; score: number }> = [];

    try {
      // Rechercher chaque terme enrichi
      for (const term of enhancedSearchTerms) {
        // Lunr utilise une syntaxe spéciale pour les recherches
        // Nous utilisons * comme joker pour trouver des correspondances partielles
        const termResults = lunrIndex.search(`${term}*`);
        lunrResults = [...lunrResults, ...termResults];
      }
    } catch (error) {
      console.error("Erreur lors de la recherche Lunr:", error);
      // En cas d'erreur, continuer avec un tableau vide
      lunrResults = [];
    }

    // Éliminer les doublons dans les résultats Lunr
    const uniqueLunrResults = lunrResults.filter(
      (result, index, self) =>
        index === self.findIndex((r) => r.ref === result.ref),
    );

    // 3. RECHERCHE AVEC SIMILARITÉ DE TEXTE
    // Utiliser notre fonction personnalisée pour comparer les textes
    const similarityResults = items
      .map((item) => {
        // Combiner tous les textes de l'item pour une meilleure analyse
        const itemFullText = [
          item.text,
          ...(item.keywords || []),
          item.context || "",
          item.comment || "",
        ]
          .join(" ")
          .toLowerCase();

        // Calculer la similarité entre les textes avec notre fonction personnalisée
        const similarity = calculateTextSimilarity(
          searchTerm.toLowerCase(),
          itemFullText,
        );

        return { item, score: similarity };
      })
      .filter((result) => result.score > 0.1); // Filtrer les scores trop bas

    // Trier par score décroissant
    similarityResults.sort((a, b) => b.score - a.score);

    // 4. COMBINER LES RÉSULTATS DES 3 MOTEURS
    const allResultsPaths = new Set([
      ...uniqueFuseResults.map((r) => r.item.path),
      ...uniqueLunrResults.map((r) => r.ref),
      ...similarityResults.map((r) => r.item.path),
    ]);

    // Créer une map pour accéder aux scores par chemin
    const fuseScores = new Map(
      uniqueFuseResults.map((r) => [r.item.path, r.score || 1]),
    );
    const lunrScores = new Map(uniqueLunrResults.map((r) => [r.ref, r.score]));
    const similarityScores = new Map(
      similarityResults.map((r) => [r.item.path, r.score]),
    );

    // Fonction pour obtenir l'item à partir de son chemin
    const getItemByPath = (path: string): FuseItem | undefined => {
      return items.find((item) => item.path === path);
    };

    // Combiner les résultats avec des poids pour chaque moteur
    const combinedResults: MultiEngineSearchResult[] = [];

    Array.from(allResultsPaths).forEach((path) => {
      const item = getItemByPath(path);
      if (!item) return;

      // Obtenir les scores de chaque moteur (0 si non trouvé)
      const fuseScore = 1 - (fuseScores.get(path) || 1); // Inverser le score Fuse (1 = meilleur)
      const lunrScore = lunrScores.get(path) || 0;
      const similarityScore = similarityScores.get(path) || 0;

      // Normaliser le score Lunr (peut être très élevé)
      const normalizedLunrScore =
        lunrScore > 0 ? Math.min(lunrScore / 10, 1) : 0;

      // Pondération des moteurs (ajustable selon les besoins)
      const weights = {
        fuse: 0, // Poids de 0% pour Fuse.js comme demandé
        lunr: 0.5, // Augmenté pour compenser la suppression de Fuse.js
        similarity: 0.5, // Augmenté pour compenser la suppression de Fuse.js
      };

      // Calculer le score combiné pondéré
      let denominateur = 0;
      if (normalizedLunrScore > 0) denominateur += weights.lunr;
      if (similarityScore > 0) denominateur += weights.similarity;

      const combinedScore =
        denominateur === 0
          ? 0
          : (normalizedLunrScore * weights.lunr +
              similarityScore * weights.similarity) /
            denominateur;

      combinedResults.push({
        path: path,
        value: item.value,
        similarity: Math.max(fuseScore, normalizedLunrScore, similarityScore), // Le meilleur score individuel
        engines: {
          fuse: fuseScore,
          lunr: normalizedLunrScore,
          similarity: similarityScore,
        },
        combinedScore: combinedScore,
      });
    });

    // Trier par score combiné décroissant et filtrer les résultats < 50%
    const filteredResults = combinedResults
      .sort((a, b) => b.combinedScore - a.combinedScore)
      .filter((result) => result.combinedScore >= 0.2); // Ne garder que les résultats > 20%

    // Amélioration: Regrouper les résultats par chemin parent pour éviter la duplication
    // et favoriser les chemins plus courts et plus pertinents
    const groupedResults: { [parentPath: string]: MultiEngineSearchResult[] } =
      {};

    filteredResults.forEach((result) => {
      // Extraire le chemin parent (jusqu'au dernier point ou crochet)
      // eslint-disable-next-line no-useless-escape
      const parentPathMatch = result.path.match(/(.*)[.\[].*$/);
      const parentPath = parentPathMatch ? parentPathMatch[1] : result.path;

      if (!groupedResults[parentPath]) {
        groupedResults[parentPath] = [];
      }

      groupedResults[parentPath].push(result);
    });

    // Pour chaque groupe, ne garder que le meilleur résultat
    const bestResults: MultiEngineSearchResult[] = [];

    Object.values(groupedResults).forEach((group) => {
      // Trier le groupe par score et prendre le meilleur
      group.sort((a, b) => b.combinedScore - a.combinedScore);
      bestResults.push(group[0]);

      // Si le groupe contient plus de 3 résultats avec un bon score (>0.7),
      // ajouter également le deuxième meilleur résultat
      if (group.length > 3 && group[1] && group[1].combinedScore > 0.7) {
        bestResults.push(group[1]);
      }
    });

    // Trier à nouveau les meilleurs résultats par score
    bestResults.sort((a, b) => b.combinedScore - a.combinedScore);

    // Limiter à 20 résultats maximum pour éviter de surcharger l'interface
    return bestResults.slice(0, 20);
  };

  // Fonction pour effectuer une nouvelle recherche
  const performSearch = () => {
    // Utiliser uniquement rawData pour la recherche
    if (Object.keys(rawData || {}).length === 0) {
      setNlpSearchResults([]);
      setSelectedResultIndex(-1);
      return;
    }

    // Filtrer les résultats pour n'afficher que ceux avec un score > 20%
    const results = findSimilarTerms(searchInput, rawData);

    // Trier les résultats par score combiné décroissant
    const sortedResults = results.sort((a, b) => {
      const multiEngineA = a as MultiEngineSearchResult;
      const multiEngineB = b as MultiEngineSearchResult;
      return multiEngineB.combinedScore - multiEngineA.combinedScore;
    });

    // Filtrer pour n'afficher que les résultats pertinents
    const filteredResults = sortedResults.filter((result) => {
      const multiEngineResult = result as MultiEngineSearchResult;
      return multiEngineResult.combinedScore >= 0.2;
    });

    setNlpSearchResults(filteredResults);
    setSelectedResultIndex(filteredResults.length > 0 ? 0 : -1);
  };

  // Fonction pour ouvrir la modal NLP avec les résultats de recherche
  const openNlpSearch = (fieldName: string) => {
    setCurrentSearchField(fieldName);
    setSearchInput(fieldName); // Initialiser l'entrée de recherche avec le nom du champ

    // Vérifier si des données brutes sont disponibles
    if (Object.keys(rawData || {}).length === 0) {
      console.log(
        "ATTENTION: Aucune donnée brute disponible pour la recherche",
      );
      setNlpSearchResults([]);
      setSelectedResultIndex(-1);
      setNlpModalOpen(true);
      return;
    }

    const results = findSimilarTerms(fieldName, rawData);
    setNlpSearchResults(results);
    setSelectedResultIndex(results.length > 0 ? 0 : -1);
    setNlpModalOpen(true);
  };

  // Fonction pour naviguer vers le résultat précédent
  const goToPrevResult = () => {
    if (nlpSearchResults.length === 0) return;
    setSelectedResultIndex((prev) =>
      prev <= 0 ? nlpSearchResults.length - 1 : prev - 1,
    );
  };

  // Fonction pour naviguer vers le résultat suivant
  const goToNextResult = () => {
    if (nlpSearchResults.length === 0) return;
    setSelectedResultIndex((prev) =>
      prev >= nlpSearchResults.length - 1 ? 0 : prev + 1,
    );
  };

  // Effet pour faire défiler jusqu'au résultat sélectionné
  useEffect(() => {
    if (selectedResultIndex >= 0 && resultListRef.current) {
      const listItems =
        resultListRef.current.querySelectorAll(".list-group-item");
      if (listItems[selectedResultIndex]) {
        listItems[selectedResultIndex].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);

  // Fonction pour sélectionner un chemin et fermer la modal
  const selectPath = (path: string) => {
    handleSelectorChange(currentSearchField as keyof Selectors, {
      value: path,
      type: "Path",
    });
    setNlpModalOpen(false);
  };

  // Fonction pour analyser un champ en arrière-plan sans ouvrir de pop-up
  const analyzeFieldInBackground = (fieldName: string) => {
    // Vérifier si des données brutes sont disponibles
    if (Object.keys(rawData || {}).length === 0) {
      console.log(
        "ATTENTION: Aucune donnée brute disponible pour la recherche",
      );
      setFieldScores((prev) => ({
        ...prev,
        [fieldName]: 0,
      }));
      return;
    }

    // Effectuer la recherche en arrière-plan
    const results = findSimilarTerms(fieldName, rawData);

    // Filtrer les résultats pour n'inclure que ceux avec un score >= 50%
    const filteredResults = results.filter((result) => {
      const multiEngineResult = result as MultiEngineSearchResult;
      return multiEngineResult.combinedScore >= 0.5;
    });

    // Mettre à jour le score du champ
    setFieldScores((prev) => ({
      ...prev,
      [fieldName]: filteredResults.length,
    }));
  };

  // Fonction pour démarrer/arrêter la boucle de parcours des champs
  const toggleFieldLoop = () => {
    if (isLooping) {
      // Arrêter la boucle mais conserver les scores
      if (loopInterval) {
        clearInterval(loopInterval);
        setLoopInterval(null);
      }
      setIsLooping(false);
      setCurrentLoopField(null);
      setLoopCounter(0);
      // Ne pas réinitialiser fieldScores pour conserver les résultats
    } else {
      // Démarrer la boucle
      setIsLooping(true);
      // Réinitialiser les scores uniquement au démarrage d'une nouvelle recherche
      setFieldScores({});

      // Récupérer tous les champs visibles selon le filtre actuel
      const visibleFields: string[] = [];
      Object.entries(fieldsByContext).forEach(([context, fields]) => {
        if (expandedContexts[context]) {
          const filteredFields = filterFields(fields);
          filteredFields.forEach((field) => {
            visibleFields.push(field.name as string);
          });
        }
      });

      if (visibleFields.length === 0) {
        alert("Aucun champ visible à parcourir");
        setIsLooping(false);
        return;
      }

      // Commencer par le premier champ
      const firstField = visibleFields[0];
      setCurrentLoopField(firstField);
      setLoopCounter(1);

      // Analyser le premier champ immédiatement
      analyzeFieldInBackground(firstField);

      // Créer un intervalle pour passer au champ suivant toutes les 100ms
      let currentIndex = 0;
      const interval = setInterval(() => {
        // Passer au champ suivant
        currentIndex++;

        // Si on a parcouru tous les champs, arrêter la boucle
        if (currentIndex >= visibleFields.length) {
          clearInterval(interval);
          setLoopInterval(null);
          setIsLooping(false);
          setCurrentLoopField(null);
          console.log(
            "Tous les champs ont été parcourus, conservation des scores:",
            fieldScores,
          );
          return;
        }

        const nextField = visibleFields[currentIndex];
        setCurrentLoopField(nextField);

        // Analyser le champ en arrière-plan sans ouvrir de pop-up
        analyzeFieldInBackground(nextField);

        // Incrémenter le compteur de boucle
        setLoopCounter((prev) => prev + 1);
      }, 100); // 100ms entre chaque champ pour une boucle encore plus rapide

      setLoopInterval(interval);
    }
  };

  // Nettoyer l'intervalle lors du démontage du composant
  useEffect(() => {
    return () => {
      if (loopInterval) {
        clearInterval(loopInterval);
      }
    };
  }, [loopInterval]);

  // Fonction pour gérer le résultat du glisser-déposer
  const handleDragEnd = (result: any, fieldName: keyof Selectors) => {
    // Si pas de destination ou si on dépose au même endroit, ne rien faire
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    const fieldSelector = selectors[fieldName];
    if (!fieldSelector?.lodashFns) return;

    // Créer une copie des fonctions Lodash
    const updatedFns = [...fieldSelector.lodashFns];

    // Retirer l'élément de la position source
    const [removed] = updatedFns.splice(result.source.index, 1);

    // Le réinsérer à la position de destination
    updatedFns.splice(result.destination.index, 0, removed);

    // Mettre à jour l'état
    handleLodashFnChange(fieldName, updatedFns);
  };

  return (
    <div>
      <h2>Configuration des Sélecteurs</h2>
      <div className="d-flex justify-content-between mb-3">
        <div>
          <Button
            color={isJsonEditor ? "secondary" : "primary"}
            onClick={() => {
              if (isJsonEditor) {
                // Soumettre les modifications JSON
                try {
                  const parsed = JSON.parse(jsonInput);
                  // Filtrer et valider les sélecteurs
                  const validatedSelectors = Object.entries(parsed).reduce<
                    Record<keyof CarDetails, Selector>
                  >(
                    (acc, [key, value]) => {
                      if (!carDetailsKeys.includes(key as keyof CarDetails)) {
                        return acc;
                      }
                      return {
                        ...acc,
                        [key]: {
                          type: "Path",
                          value: "",
                          ...(value && typeof value === "object" ? value : {}),
                        } as Selector,
                      };
                    },
                    {} as Record<keyof CarDetails, Selector>,
                  );
                  setSelectors(validatedSelectors);
                  setJsonError(null);
                  setIsJsonEditor(false);
                } catch (error: any) {
                  setJsonError("JSON invalide : " + error.message);
                }
              } else {
                // Passer à l'éditeur JSON
                setJsonInput(JSON.stringify(selectors, null, 2));
                setJsonError(null);
                setIsJsonEditor(true);
              }
            }}
          >
            {isJsonEditor ? "Retour à l'Éditeur Visuel" : "Éditer en JSON"}
          </Button>

          {/* Bouton pour parcourir en boucle les champs */}
          <Button
            color={isLooping ? "danger" : "success"}
            className="ml-2"
            onClick={toggleFieldLoop}
            disabled={isJsonEditor}
          >
            <FontAwesomeIcon
              icon={isLooping ? faTimes : faChevronRight}
              className="mr-1"
            />
            {isLooping ? "Stop auto-recherche" : "Recherche auto"}
          </Button>
        </div>

        {!isJsonEditor && (
          <div className="filter-container d-flex align-items-center">
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            <Select
              value={{
                value: fieldFilter,
                label:
                  fieldFilter === "all"
                    ? "Tous les champs"
                    : fieldFilter === "configured"
                      ? "Champs paramétrés"
                      : fieldFilter === "unconfigured"
                        ? "Champs non paramétrés"
                        : fieldFilter === "withResult"
                          ? "Champs avec résultat"
                          : "Champs sans résultat",
              }}
              onChange={(option: any) => setFieldFilter(option.value)}
              options={[
                { value: "all", label: "Tous les champs" },
                { value: "configured", label: "Champs paramétrés" },
                { value: "unconfigured", label: "Champs non paramétrés" },
                { value: "withResult", label: "Champs avec résultat" },
                { value: "withoutResult", label: "Champs sans résultat" },
              ]}
              className="filter-select"
              placeholder="Filtrer les champs..."
              isSearchable={false}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: "250px",
                }),
                control: (provided) => ({
                  ...provided,
                  minWidth: "250px",
                  maxWidth: "250px",
                }),
                menu: (provided) => ({
                  ...provided,
                  minWidth: "250px",
                  maxWidth: "250px",
                }),
              }}
            />
          </div>
        )}
      </div>

      {isJsonEditor ? (
        <div>
          {jsonError && <Alert color="danger">{jsonError}</Alert>}
          <FormGroup>
            <Input
              type="textarea"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              style={{ minHeight: "400px", fontFamily: "monospace" }}
            />
          </FormGroup>
        </div>
      ) : (
        <div>
          <div className="main-selectors-container mb-3">
            {/* add main selector with input to add name */}
            <Card>
              <CardBody className="p-3">
                <Row className="d-flex align-items-center">
                  <Col xs="8" md="9">
                    <Input
                      name="mainSelectorName"
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Nom du sélecteur principal"
                      className="form-control"
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddMainSelector()
                      }
                    />
                  </Col>
                  <Col xs="4" md="3" className="pl-0">
                    <Button
                      color="primary"
                      block
                      onClick={handleAddMainSelector}
                      disabled={inputValue.trim() === ""}
                      className="d-flex align-items-center justify-content-center"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Ajouter
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </div>
          {Object.entries(fieldsByContext).map(([context, fields]) => {
            // Filtrer les champs selon le critère sélectionné
            const filteredFields = filterFields(fields);

            // Ne pas afficher les contextes vides après filtrage
            if (filteredFields.length === 0) return null;

            return (
              <Card key={context} className="mb-3">
                <CardHeader
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleContext(context)}
                  title="Cliquez pour afficher/masquer cette section"
                >
                  <h5 className="mb-0 d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={
                        expandedContexts[context]
                          ? faChevronDown
                          : faChevronRight
                      }
                      className="toggle-icon mr-2"
                    />
                    {context}
                  </h5>
                  <span className="text-muted small">
                    {filteredFields.length} champ
                    {filteredFields.length > 1 ? "s" : ""}
                  </span>
                </CardHeader>
                <Collapse isOpen={expandedContexts[context]}>
                  <CardBody className="p-0">
                    <Table
                      size="sm"
                      bordered
                      hover
                      responsive
                      className="mb-0"
                      style={{ width: "100%", tableLayout: "fixed" }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: "20%" }}>Champ</th>
                          <th style={{ width: "20%" }}>Valeur du Sélecteur</th>
                          <th style={{ width: "20%" }}>Fonctions Lodash</th>
                          <th style={{ width: "20%" }}>Résultat Parsé</th>
                          <th style={{ width: "20%" }}>Erreurs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFields.map((field) => {
                          const fieldName = field.name as keyof Selectors;
                          const fieldSelector = selectors[fieldName] || {
                            value: "",
                            type: "Path",
                          };

                          const status = getStatus(fieldName);
                          const statusText = getStatusText(fieldName);
                          const tooltipId = `tooltip-${String(fieldName)}`;

                          return (
                            <tr
                              key={String(fieldName)}
                              id={`field-row-${String(fieldName)}`}
                              className={
                                currentLoopField === String(fieldName)
                                  ? "bg-light"
                                  : ""
                              }
                            >
                              <td className="field-cell">
                                <div className="d-flex align-items-center">
                                  <Badge
                                    color={
                                      status === "success"
                                        ? "success"
                                        : "warning"
                                    }
                                    className="badge-label mr-2"
                                    id={tooltipId}
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      openNlpSearch(String(fieldName))
                                    }
                                  >
                                    {String(fieldName)}
                                  </Badge>
                                  <Button
                                    color="link"
                                    className="p-0 ml-1"
                                    title="Rechercher des termes similaires"
                                    onClick={() =>
                                      openNlpSearch(String(fieldName))
                                    }
                                  >
                                    <FontAwesomeIcon
                                      icon={faSearch}
                                      size="sm"
                                    />
                                  </Button>
                                  {/* Indicateur de score pour la boucle - toujours visible si un score existe */}
                                  {fieldScores[String(fieldName)] !==
                                    undefined && (
                                    <Badge
                                      color={
                                        fieldScores[String(fieldName)] > 0
                                          ? "info"
                                          : "secondary"
                                      }
                                      pill
                                      className="ml-1"
                                      title={`${fieldScores[String(fieldName)]} résultat(s) trouvé(s)`}
                                    >
                                      {fieldScores[String(fieldName)]}
                                    </Badge>
                                  )}
                                  <Tooltip
                                    placement="right"
                                    isOpen={tooltipOpen[tooltipId]}
                                    target={tooltipId}
                                    toggle={() => toggleTooltipState(tooltipId)}
                                  >
                                    {statusText}
                                  </Tooltip>
                                </div>
                              </td>
                              <td className="p-1">
                                <ValueSelector
                                  fieldName={fieldName}
                                  selector={fieldSelector}
                                  handleSelectorChange={handleSelectorChange}
                                />
                              </td>

                              <td>
                                <LodashFunctionSelector
                                  lodashFn=""
                                  addLodashFn={(lodashFn) =>
                                    handleLodashAppendFn(fieldName, {
                                      id: uuid(),
                                      value: lodashFn,
                                      args: [],
                                    })
                                  }
                                />
                                {/* Afficher les fonctions Lodash sélectionnées avec drag & drop */}
                                {fieldSelector.lodashFns &&
                                  fieldSelector.lodashFns.length > 0 && (
                                    <div className="lodash-functions-container mt-2 p-2 border rounded">
                                      <DragDropContext
                                        onDragEnd={(result) =>
                                          handleDragEnd(result, fieldName)
                                        }
                                      >
                                        <Droppable
                                          droppableId={`lodash-list-${String(fieldName)}`}
                                        >
                                          {(provided) => (
                                            <div
                                              {...provided.droppableProps}
                                              ref={provided.innerRef}
                                            >
                                              {fieldSelector.lodashFns!.map(
                                                (
                                                  fn: LodashFunction,
                                                  index: number,
                                                ) => (
                                                  <Draggable
                                                    key={fn.id}
                                                    draggableId={fn.id}
                                                    index={index}
                                                  >
                                                    {(provided, snapshot) => (
                                                      <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`lodash-function-item mb-2 pb-2 border-bottom ${
                                                          snapshot.isDragging
                                                            ? "dragging"
                                                            : ""
                                                        }`}
                                                      >
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                          <div className="d-flex align-items-center">
                                                            <span
                                                              {...provided.dragHandleProps}
                                                              className="drag-handle mr-2"
                                                              title="Glisser pour réordonner"
                                                            >
                                                              <FontAwesomeIcon
                                                                icon={
                                                                  faGripVertical
                                                                }
                                                              />
                                                            </span>
                                                            <span className="badge p-2 lodash-function-badge">
                                                              <FontAwesomeIcon
                                                                icon={faCode}
                                                                className="mr-1"
                                                              />
                                                              {fn.value}
                                                            </span>
                                                          </div>
                                                          <Button
                                                            size="sm"
                                                            className="delete-button"
                                                            title="Supprimer cette fonction"
                                                            onClick={() => {
                                                              handleLodashRemoveFn(
                                                                fieldName,
                                                                fn.id,
                                                              );
                                                            }}
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={faTrash}
                                                            />
                                                          </Button>
                                                        </div>

                                                        {/* Arguments de la fonction */}
                                                        <div className="arguments-container">
                                                          {(fn.args || []).map(
                                                            (arg, argIndex) => (
                                                              <div
                                                                key={argIndex}
                                                                className="argument-row"
                                                              >
                                                                <Input
                                                                  type="text"
                                                                  value={arg}
                                                                  onChange={(
                                                                    e,
                                                                  ) => {
                                                                    const updatedFns =
                                                                      [
                                                                        ...fieldSelector.lodashFns!,
                                                                      ];
                                                                    const updatedArgs =
                                                                      [
                                                                        ...updatedFns[
                                                                          index
                                                                        ].args!,
                                                                      ];
                                                                    updatedArgs[
                                                                      argIndex
                                                                    ] =
                                                                      e.target.value;
                                                                    updatedFns[
                                                                      index
                                                                    ].args =
                                                                      updatedArgs;
                                                                    handleLodashFnChange(
                                                                      fieldName,
                                                                      updatedFns,
                                                                    );
                                                                  }}
                                                                  placeholder={`Arg ${argIndex + 1}`}
                                                                  bsSize="sm"
                                                                  className="mr-1"
                                                                />
                                                                <Button
                                                                  size="sm"
                                                                  className="delete-button"
                                                                  title="Supprimer cet argument"
                                                                  onClick={() => {
                                                                    const updatedFns =
                                                                      [
                                                                        ...fieldSelector.lodashFns!,
                                                                      ];
                                                                    const updatedArgs =
                                                                      [
                                                                        ...updatedFns[
                                                                          index
                                                                        ].args!,
                                                                      ];
                                                                    updatedArgs.splice(
                                                                      argIndex,
                                                                      1,
                                                                    );
                                                                    updatedFns[
                                                                      index
                                                                    ].args =
                                                                      updatedArgs;
                                                                    handleLodashFnChange(
                                                                      fieldName,
                                                                      updatedFns,
                                                                    );
                                                                  }}
                                                                >
                                                                  <FontAwesomeIcon
                                                                    icon={
                                                                      faTrash
                                                                    }
                                                                  />
                                                                </Button>
                                                              </div>
                                                            ),
                                                          )}

                                                          {/* Bouton pour ajouter un argument */}
                                                          <Button
                                                            outline
                                                            color="secondary"
                                                            size="sm"
                                                            className="add-argument-button"
                                                            onClick={() => {
                                                              const updatedFns =
                                                                [
                                                                  ...fieldSelector.lodashFns!,
                                                                ];
                                                              updatedFns[
                                                                index
                                                              ].args = [
                                                                ...(updatedFns[
                                                                  index
                                                                ].args || []),
                                                                "",
                                                              ];
                                                              handleLodashFnChange(
                                                                fieldName,
                                                                updatedFns,
                                                              );
                                                            }}
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={faPlus}
                                                              className="mr-1"
                                                            />
                                                            Ajouter un argument
                                                          </Button>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ),
                                              )}
                                              {provided.placeholder}
                                            </div>
                                          )}
                                        </Droppable>
                                      </DragDropContext>
                                    </div>
                                  )}
                              </td>
                              <td className="truncate">
                                <span className="d-block">
                                  {parsedData[fieldName] || ""}
                                </span>
                              </td>
                              <td className="truncate">
                                <span className="d-block">
                                  {fieldSelector.type === "MainSelector" && (
                                    <Button
                                      color="link"
                                      size="sm"
                                      onClick={() =>
                                        removeMainSelector(fieldName)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  )}
                                  {errors[fieldName] || ""}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </CardBody>
                </Collapse>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal pour afficher les résultats de recherche NLP */}
      <Modal
        isOpen={nlpModalOpen}
        toggle={() => setNlpModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setNlpModalOpen(false)}>
          Recherche de termes similaires à "{currentSearchField}"
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="input-group">
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Modifier les termes de recherche..."
                onKeyPress={(e) => e.key === "Enter" && performSearch()}
              />
              <div className="input-group-append">
                <Button color="primary" onClick={performSearch}>
                  <FontAwesomeIcon icon={faSearch} /> Rechercher
                </Button>
              </div>
            </div>
            <small className="text-muted mt-1 d-block">
              Conseil: Essayez des termes plus courts ou des synonymes si vous
              ne trouvez pas de résultats.
            </small>
          </div>

          {/* Afficher les données brutes pour le débogage */}
          {process.env.NODE_ENV === "development" && (
            <div className="mb-3">
              <details>
                <summary className="text-primary" style={{ cursor: "pointer" }}>
                  Informations de débogage
                </summary>
                <div className="mt-2 p-2 border rounded bg-light">
                  <p>
                    Terme recherché: <code>{searchInput}</code>
                  </p>
                  <p>
                    Données brutes disponibles:{" "}
                    <code>
                      {Object.keys(rawData || {}).length > 0 ? "Oui" : "Non"}
                    </code>
                  </p>
                  <p>
                    Nombre de résultats: <code>{nlpSearchResults.length}</code>
                  </p>
                </div>
              </details>
            </div>
          )}

          {nlpSearchResults.length === 0 ? (
            <Alert color="info">
              Aucun terme similaire trouvé dans les données JSON. Essayez de
              modifier les termes de recherche ou d'utiliser des mots plus
              courts.
            </Alert>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span>
                  {nlpSearchResults.length} résultat
                  {nlpSearchResults.length > 1 ? "s" : ""} trouvé
                  {nlpSearchResults.length > 1 ? "s" : ""}
                </span>
                <div>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={goToPrevResult}
                    className="mr-2"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} /> Précédent
                  </Button>
                  <Button color="secondary" size="sm" onClick={goToNextResult}>
                    Suivant <FontAwesomeIcon icon={faArrowRight} />
                  </Button>
                </div>
              </div>
              <div
                ref={resultListRef}
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <ListGroup>
                  {nlpSearchResults.map((result, index) => {
                    // Déterminer si le résultat a des scores multi-moteurs
                    const multiEngineResult = result as MultiEngineSearchResult;
                    const hasEngines = multiEngineResult.engines;

                    // Formater la valeur pour l'affichage
                    let displayValue = "";
                    if (result.value === null) {
                      displayValue = "null";
                    } else if (typeof result.value === "object") {
                      try {
                        // Pour les objets et tableaux, afficher une version abrégée
                        const jsonString = JSON.stringify(result.value);
                        displayValue =
                          jsonString.length > 100
                            ? jsonString.substring(0, 100) + "..."
                            : jsonString;
                      } catch (e) {
                        displayValue = "[Objet complexe]";
                      }
                    } else {
                      displayValue = String(result.value);
                    }

                    // Formater le chemin pour une meilleure lisibilité
                    const formattedPath = result.path
                      .replace(/\./g, " → ") // Remplacer les points par des flèches
                      .replace(/\[(\d+)\]/g, "[$1]"); // Garder les indices de tableau

                    // Déterminer si c'est un tableau ou un objet
                    const isArray = Array.isArray(result.value);
                    const isObject =
                      !isArray &&
                      typeof result.value === "object" &&
                      result.value !== null;

                    // Ajouter des informations sur le type de données
                    let typeInfo = "";
                    if (isArray) {
                      typeInfo = `Tableau (${result.value.length} éléments)`;
                    } else if (isObject) {
                      typeInfo = `Objet (${Object.keys(result.value).length} propriétés)`;
                    } else if (typeof result.value === "string") {
                      typeInfo = "Texte";
                    } else if (typeof result.value === "number") {
                      typeInfo = "Nombre";
                    } else if (typeof result.value === "boolean") {
                      typeInfo = "Booléen";
                    }

                    // Générer une prévisualisation des propriétés pour les objets
                    let propertiesPreview = "";
                    if (isObject) {
                      try {
                        const properties = Object.keys(result.value).slice(
                          0,
                          5,
                        );
                        propertiesPreview =
                          properties.join(", ") +
                          (Object.keys(result.value).length > 5 ? "..." : "");
                      } catch (e) {
                        propertiesPreview = "Propriétés non disponibles";
                      }
                    }

                    return (
                      <ListGroupItem
                        key={index}
                        active={index === selectedResultIndex}
                        onClick={() => setSelectedResultIndex(index)}
                        action
                        className="d-flex flex-column"
                      >
                        <div className="d-flex justify-content-between align-items-start w-100">
                          <div className="flex-grow-1">
                            <div className="mb-1">
                              <strong>Chemin:</strong>
                              <span className="path-display ml-1">
                                {formattedPath}
                              </span>
                            </div>
                            <div className="mb-1">
                              <strong>Type:</strong>
                              <span className="ml-1">{typeInfo}</span>
                              {isObject && (
                                <small className="text-muted ml-2">
                                  Propriétés: {propertiesPreview}
                                </small>
                              )}
                            </div>
                            <div>
                              <strong>Valeur:</strong>{" "}
                              <span
                                className="value-display ml-1"
                                style={{
                                  fontFamily: "monospace",
                                  wordBreak: "break-all",
                                }}
                              >
                                {displayValue}
                              </span>
                            </div>

                            {/* Ajouter des boutons pour explorer les propriétés imbriquées */}
                            {isObject && (
                              <div className="mt-2">
                                <small className="text-primary">
                                  Chemins suggérés:
                                </small>
                                <div className="d-flex flex-wrap mt-1">
                                  {Object.keys(result.value)
                                    .slice(0, 5)
                                    .map((key) => (
                                      <Button
                                        key={key}
                                        color="outline-secondary"
                                        size="sm"
                                        className="mr-1 mb-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          selectPath(`${result.path}.${key}`);
                                        }}
                                        title={`Sélectionner ${result.path}.${key}`}
                                      >
                                        {key}
                                      </Button>
                                    ))}
                                  {Object.keys(result.value).length > 5 && (
                                    <span className="text-muted align-self-center ml-1">
                                      +{Object.keys(result.value).length - 5}{" "}
                                      autres
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="d-flex flex-column align-items-end ml-2">
                            {/* Badge pour le score combiné uniquement */}
                            {hasEngines && hasEngines.fuse !== undefined && (
                              <Badge
                                color="primary"
                                pill
                                className="mb-2"
                                title="Score combiné"
                              >
                                <FontAwesomeIcon
                                  icon={faLayerGroup}
                                  className="mr-1"
                                />
                                {Math.round(
                                  multiEngineResult.combinedScore * 100,
                                )}
                                %
                              </Badge>
                            )}
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => selectPath(result.path)}
                            >
                              Sélectionner
                            </Button>
                          </div>
                        </div>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setNlpModalOpen(false)}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SelectorsEditor;
